import { Minus, Tick } from '@berlitz/icons'
import Pagination from '@berlitz/pagination'
import Spacer from '@berlitz/spacer'
import { Pill } from '@components/PedagogicalTable/style'
import Table, { TableLoader } from '@components/Table'
import { ColumnProps } from '@components/Table/interface'
import WithLessonUtilsHOC, { WithLessonUtilsProp } from '@hocs/WithLessonUtils'
import { LessonHistory } from '@interfaces/LessonHistory'
import { Material as IMaterial } from '@interfaces/Material'
import { GroupClassListItem } from '@interfaces/MySchedule'
import { getColorByGrade } from '@utils/helpers/grading-color'
import { determinLCSZero } from '@utils/helpers/learning-path'
import paginate from '@utils/helpers/paginate'
import find from 'lodash/find'
import get from 'lodash/get'
import sortBy from 'lodash/sortBy'
import * as React from 'react'
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl'
import { Col, Row } from 'react-styled-flexboxgrid'
import { Section } from '../Layout/style'
import { RecordTableProps } from './interface'
import {
  Box,
  ButtonContainer,
  GradeLabel,
  Label,
  List,
  OutlineButton,
  PaginationContainer,
  RecordWrapper,
  SelectLevel,
  SpeakingTitle,
  StyledH2,
  StyledSelect,
} from './style'

interface TableListProp {
  columns: Array<ColumnProps<LessonHistory>>
  lessonHistory: LessonHistory[]
}

const TableList: React.FunctionComponent<TableListProp> = ({ columns, lessonHistory }) => {
  const displayItem = 10
  const numberPages = Math.ceil(lessonHistory.length / displayItem)
  const [activePage, setActivePage] = React.useState(1)
  const [data, setData] = React.useState(paginate(lessonHistory, displayItem, activePage))

  React.useEffect(() => {
    setData(paginate(lessonHistory, displayItem, activePage))
  }, [lessonHistory, displayItem, activePage])

  const setPaginatedData = (page: number, passedHistory?: LessonHistory[]) => {
    const lh = passedHistory || lessonHistory
    setActivePage(page)
    setData(paginate(lh, displayItem, page))
  }

  return (
    <>
      <Table columns={columns} dataSource={data} />
      {lessonHistory.length > displayItem && (
        <PaginationContainer>
          <Pagination
            activePage={activePage}
            numberPages={numberPages}
            onChange={pageNumber => {
              setPaginatedData(pageNumber)
            }}
            prevLabel="Back"
            nextLabel="Next"
          />
        </PaginationContainer>
      )}
    </>
  )
}

const FlexTable: React.FunctionComponent<InjectedIntlProps & RecordTableProps & WithLessonUtilsProp> = ({
  intl,
  lessonHistory = [],
  setLessonHistory,
  instructor,
  selectedStudent,
  options,
  setStudent,
  students,
  loading,
  displayLessonsBtn,
  title = 'Pedagogical record',
  units,
  ...props
}) => {
  const [showMyLessons, setShowMyLessons] = React.useState(false)
  let defaultColumns = [
    {
      title: intl.formatMessage({ id: 'Date' }),
      dataIndex: 'date',
      key: 'date',
      align: 'left',
      width: '15%',
      // eslint-disable-next-line react/display-name
      render: (text: string, record: LessonHistory) => <Label status={record.attendance}>{text}</Label>,
    },
    {
      title: intl.formatMessage({ id: 'Material' }),
      dataIndex: 'materials',
      key: 'materials',
      align: 'left',
      // eslint-disable-next-line react/display-name
      render: (materials: IMaterial[], record: LessonHistory) => (
        <>
          {!record.isRegistered
            ? '-'
            : materials.map(
                ({ MaterialName }, index) =>
                  MaterialName && (
                    <List key={`${MaterialName}-${index}`} showBullet={materials.length > 1} status={record.attendance}>
                      <li>
                        <div>
                          <Label status={record.attendance}>{MaterialName}</Label>
                          <Spacer size="xs" />
                        </div>
                      </li>
                    </List>
                  )
              )}
        </>
      ),
    },
    {
      title: intl.formatMessage({ id: 'Live coaching session' }),
      dataIndex: 'description',
      key: 'description',
      align: 'left',
      width: '20%',
      render: (text: string, record: LessonHistory) =>
        !record.isRegistered ? (
          '-'
        ) : (
          <Label status={record.attendance}>{record.attendance === 'Absent' ? <>({text})</> : <>{text}</>}</Label>
        ),
    },
    {
      title: intl.formatMessage({ id: 'Attendance' }),
      dataIndex: 'attendance',
      key: 'attendance',
      align: 'left',
      render: (text: string, record: LessonHistory) =>
        !record.isRegistered ? (
          '-'
        ) : (
          <Label status={record.attendance}>
            {record.attendance === 'Absent' ? <>({text})</> : <>{text || '-'}</>}
          </Label>
        ),
    },
    {
      title: intl.formatMessage({ id: 'Lesson' }),
      dataIndex: 'goals',
      key: 'goals',
      align: 'left',
      // eslint-disable-next-line react/display-name
      render: (_text: string, record: LessonHistory) => {
        const isProgramOnline = props.isProgramOnline({ Type: record.type } as GroupClassListItem)
        const speakingGoals = sortBy(record.speakingGoals, 'RecordId')
        const isLcs0 = determinLCSZero(record.unitId || '', units)
        return (
          <>
            {!record.isRegistered ? (
              '-'
            ) : !isLcs0 ? (
              speakingGoals.map((sg, index) => (
                <React.Fragment key={index}>
                  <SpeakingTitle>
                    <FormattedMessage
                      id="Lesson #"
                      defaultMessage="Lesson #{count}: {description}"
                      values={{ count: index + 1, description: sg.Title }}
                    />
                  </SpeakingTitle>
                  <RecordWrapper key={index} isFlex>
                    {sg.IsGoalAchieved && record.attendance !== 'Absent' && (
                      <Pill success spaceRight>
                        <Tick />
                      </Pill>
                    )}
                    {!sg.IsGoalAchieved && record.attendance !== 'Absent' && (
                      <>
                        <Pill warning={isProgramOnline} spaceRight>
                          <Minus />
                        </Pill>

                        <div>
                          {sg.GoalNotAchieved.map(goal => (
                            <Pill warning={isProgramOnline} key={goal} spaceRight>
                              <FormattedMessage id={goal} />
                            </Pill>
                          ))}
                        </div>
                      </>
                    )}
                  </RecordWrapper>
                </React.Fragment>
              ))
            ) : (
              <Pill success spaceRight>
                <Tick />
              </Pill>
            )}
          </>
        )
      },
    },
    {
      title: intl.formatMessage({ id: 'Instructor' }),
      dataIndex: 'instructor',
      key: 'instructor',
      align: 'left',
      // eslint-disable-next-line react/display-name
      render: (text: string, record: LessonHistory) => <Label status={record.attendance}>{text}</Label>,
    },

    {
      title: intl.formatMessage({ id: 'Score' }),
      dataIndex: 'grading',
      key: 'grading',
      align: 'left',
      // eslint-disable-next-line react/display-name
      render: (text: string, record: LessonHistory) =>
        text ? <GradeLabel color={getColorByGrade(record.grading)}>{text + '%'}</GradeLabel> : '-',
    },
  ]

  return (
    <Section>
      <Box>
        <Row>
          <Col xs={12} md={8}>
            <StyledH2>{intl.formatMessage({ id: title })}</StyledH2>
          </Col>
          <Col xs={12} md={4}>
            <ButtonContainer>
              {displayLessonsBtn && (
                <OutlineButton
                  outline={!showMyLessons}
                  onClick={() => {
                    const isShown = !showMyLessons
                    setShowMyLessons(isShown)

                    if (isShown) {
                      const name = [
                        get(instructor, 'InstructorProfile.instructorInfo.contactInfo.FirstName'),
                        get(instructor, 'InstructorProfile.instructorInfo.contactInfo.LastName'),
                      ].join(' ')

                      const lessons = lessonHistory.filter(lesson => lesson.instructor === name)
                      setLessonHistory(lessons)
                    } else {
                      setLessonHistory()
                    }
                  }}
                >
                  <FormattedMessage id="My lessons" />
                </OutlineButton>
              )}
            </ButtonContainer>
          </Col>
          {options.length > 1 && (
            <Col xs={12} md={2}>
              <SelectLevel>
                <StyledSelect
                  label=""
                  name="Student"
                  value={get(selectedStudent, 'CustomerId')}
                  onChange={value => {
                    const student = find(students, { CustomerId: value })
                    if (student) {
                      setStudent(student)
                    }
                  }}
                  options={options}
                  disableMargin
                />
              </SelectLevel>
            </Col>
          )}
        </Row>
        {loading && <TableLoader />}
        {!loading && <TableList columns={defaultColumns} lessonHistory={lessonHistory} />}
      </Box>
    </Section>
  )
}

export default WithLessonUtilsHOC(injectIntl(FlexTable))
